import { useState, useRef, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { IonSpinner } from '@ionic/react';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { useTranslation } from 'react-i18next';
import { uploadAvatar, deleteAvatar } from '../../services/profileService';
import { useProfileStore } from '../../store/useProfileStore';
import { getCroppedImg } from '../../utils/cropImage';
import { Modal } from '../ui/Modal';

interface AvatarUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AvatarUploadModal: React.FC<AvatarUploadModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('profile');
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSourcePicker, setShowSourcePicker] = useState(false);

  const hasAvatar = !!activeProfile?.avatarUrl;

  const reset = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedArea(null);
    setError('');
    setLoading(false);
    setShowSourcePicker(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onCropComplete = useCallback((_: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setShowSourcePicker(false);
    };
    reader.readAsDataURL(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleTakePhoto = async () => {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
        quality: 90,
      });
      if (photo.dataUrl) {
        setImageSrc(photo.dataUrl);
        setShowSourcePicker(false);
      }
    } catch {
      // User cancelled camera
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedArea) return;
    setError('');
    setLoading(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedArea);
      const file = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      await uploadAvatar(file);
      handleClose();
    } catch {
      setError(t('avatar.errorUpload'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setError('');
    setLoading(true);
    try {
      await deleteAvatar();
      handleClose();
    } catch {
      setError(t('avatar.errorDelete'));
    } finally {
      setLoading(false);
    }
  };

  if (!activeProfile) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="sm" surfaceClassName="bg-white" zIndexClass="z-50">
      <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">{t('avatar.title')}</h2>

        {/* Square preview / cropper area */}
        <div className="flex justify-center mb-4">
          <div
            className="relative overflow-hidden bg-gray-100"
            style={{ width: 150, height: 150 }}
          >
            {imageSrc ? (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                style={{
                  containerStyle: { width: 150, height: 150 },
                }}
              />
            ) : hasAvatar ? (
              <img
                src={activeProfile.avatarUrl}
                alt={activeProfile.displayName}
                className="w-full h-full object-cover"
              />
            ) : null}

            {/* Camera icon overlay — only when not cropping */}
            {!imageSrc && (
              <button
                type="button"
                onClick={() => setShowSourcePicker(true)}
                className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-12 h-12 text-white/70"
                >
                  <path d="M12 9a3.75 3.75 0 100 7.5A3.75 3.75 0 0012 9z" />
                  <path
                    fillRule="evenodd"
                    d="M9.344 3.071a49.52 49.52 0 015.312 0c.967.052 1.83.585 2.332 1.39l.821 1.317c.24.383.645.643 1.11.71.386.054.77.113 1.152.177 1.432.239 2.429 1.493 2.429 2.909V18a3 3 0 01-3 3H4.5a3 3 0 01-3-3V9.574c0-1.416.997-2.67 2.429-2.909.382-.064.766-.123 1.152-.177a1.56 1.56 0 001.11-.71l.822-1.315a2.942 2.942 0 012.332-1.39zM6.75 12.75a5.25 5.25 0 1110.5 0 5.25 5.25 0 01-10.5 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Source picker dropdown */}
        {showSourcePicker && (
          <div className="flex flex-col gap-2 mb-4">
            <button
              type="button"
              onClick={handleTakePhoto}
              className="w-full py-2 px-4 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('avatar.takePhotoCta')}
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-2 px-4 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('avatar.uploadCta')}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        )}

        {error && <p className="text-red-600 text-sm text-center mb-4">{error}</p>}

        {/* Action buttons */}
        <div className="flex items-center justify-between">
          <div>
            {hasAvatar && !imageSrc && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
              >
                {t('common:actions.delete')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common:actions.cancel')}
            </button>
            {imageSrc && (
              <button
                type="button"
                onClick={handleSave}
                disabled={loading || !croppedArea}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <IonSpinner name="crescent" className="w-4 h-4" />
                    {t('avatar.savingState')}
                  </>
                ) : (
                  t('common:actions.save')
                )}
              </button>
            )}
          </div>
        </div>
    </Modal>
  );
};
